import { MoreHoriz } from "@mui/icons-material";
import {
  Box,
  Breakpoint,
  Menu,
  MenuItem,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";

export interface TabItem {
  label: string;
  value: string;
}

export type TabWidth = Partial<Record<Breakpoint, string>> | string;

interface CustomTabsProps {
  activeTab: string;
  onTabChange: (event: React.SyntheticEvent, newValue: string) => void;
  tabs: TabItem[];
  tabWidth?: TabWidth;
  indicatorHeight?: number;
  containerSx?: SxProps<Theme>;
}

// --- MOBILE NAVIGATION ---
interface MobileTabNavigationProps {
  availableTabs: TabItem[];
  currentTab: string;
  onTabChange: (value: string) => void;
  containerSx?: SxProps<Theme>;
}

const MobileTabNavigation: React.FC<MobileTabNavigationProps> = ({
  availableTabs,
  currentTab,
  onTabChange,
  containerSx,
}) => {
  const theme = useTheme();
  const isNarrowMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const tabContainerRef = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(
      isNarrowMobile && tabContainerRef.current
        ? tabContainerRef.current
        : event.currentTarget,
    );
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileTabSelect = (value: string) => {
    onTabChange(value);
    handleMenuClose();
  };

  if (availableTabs.length <= 1) {
    return null;
  }

  return (
    <Box sx={{ mb: 4, ...containerSx }}>
      <Box
        ref={tabContainerRef}
        sx={{ display: "flex", width: "100%", alignItems: "flex-end" }}
      >
        {/* Active Tab Area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              px: 2,
              pb: 1,
              display: "flex",
              alignItems: "center",
              height: 32,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "20px",
                color: theme.colors.primary[5],
              }}
            >
              {availableTabs.find((tab) => tab.value === currentTab)?.label}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 4,
              backgroundColor: theme.colors.primary[5],
            }}
          />
        </Box>

        {/* More Options Button Area */}
        <Box
          aria-controls={isMenuOpen ? "custom-tabs-menu" : undefined}
          aria-expanded={isMenuOpen ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleMenuClick}
          sx={{
            width: 56,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              pb: 1,
              display: "flex",
              alignItems: "center",
              height: 32,
            }}
          >
            <MoreHoriz sx={{ color: theme.colors.primary[5] }} />
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 1,
              backgroundColor: theme.colors.secondary[2],
            }}
          />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: isNarrowMobile ? "center" : "right",
          vertical: "bottom",
        }}
        id="custom-tabs-menu"
        onClose={handleMenuClose}
        open={isMenuOpen}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              mt: 1,
              boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.1)",
              width:
                isNarrowMobile && anchorEl ? anchorEl.clientWidth : undefined,
              minWidth: isNarrowMobile ? undefined : 195,
              p: 1,
              "& .MuiList-root": { p: 0 },
              "& .MuiMenuItem-root": {
                fontSize: 14,
                fontWeight: 600,
                color: theme.colors.text[1],
                borderRadius: "8px",
                mb: "2px",
                px: 2,
                py: 1.5,
              },
              "& .MuiMenuItem-root:hover": {
                backgroundColor: theme.colors.secondary[0],
                color: theme.colors.text[6],
              },
            },
          },
        }}
        transformOrigin={{
          horizontal: isNarrowMobile ? "center" : "right",
          vertical: "top",
        }}
      >
        {availableTabs
          .filter((tab) => tab.value !== currentTab)
          .map((tab) => (
            <MenuItem
              key={tab.value}
              onClick={() => handleMobileTabSelect(tab.value)}
            >
              {tab.label}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

// --- EXPORTED COMPONENT ---
export const CustomTabs: React.FC<CustomTabsProps> = ({
  activeTab,
  onTabChange,
  tabs,
  tabWidth = { xs: "fit-content", md: "143px" },
  indicatorHeight = 4,
  containerSx,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  // If on mobile, return the new mobile navigation design
  if (isMobile) {
    return (
      <MobileTabNavigation
        availableTabs={tabs}
        containerSx={containerSx}
        currentTab={activeTab}
        onTabChange={(newValue) => {
          onTabChange({} as React.SyntheticEvent, newValue);
        }}
      />
    );
  }

  // Otherwise, return standard desktop tabs
  return (
    <Box
      sx={{
        borderBottom: 2,
        borderColor: "divider",
        marginBottom: "32px",
        ...containerSx,
      }}
    >
      <Tabs
        onChange={onTabChange}
        sx={{
          marginBottom: "-2px",
          "& .MuiTabs-indicator": {
            display: "flex",
            justifyContent: "center",
            backgroundColor: theme.colors.primary[5],
            height: indicatorHeight,
          },
        }}
        value={activeTab}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            sx={{
              width: tabWidth,
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              color: theme.colors.text[1],
              position: "relative",
              "&.Mui-selected": {
                color: theme.colors.primary[5],
              },
              "&:not(.Mui-selected):hover::after": {
                content: { xs: "none", md: '""' },
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: `${indicatorHeight}px`,
                backgroundColor: theme.colors.secondary[5],
              },
            }}
            value={tab.value}
          />
        ))}
      </Tabs>
    </Box>
  );
};
